import React from 'react';
import { motion } from 'framer-motion';
import { Users, Briefcase, Code, Server, Palette, TrendingUp, Globe, Megaphone, Target, Zap } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ServicesPage = () => {
  const mainServices = [
    {
      icon: Users,
      title: 'Recruitment and Staffing',
      description: 'Connect with top-tier talent across all industries. Our expert recruitment team specializes in finding the perfect match between exceptional candidates and world-class opportunities.',
      features: [
        'Executive Search & Headhunting',
        'Permanent Placement Services',
        'Contract & Temporary Staffing',
        'Industry-Specific Recruitment',
        'Talent Pool Management',
        'Candidate Screening & Assessment'
      ],
      gradient: 'from-cyan-500/20 to-blue-500/20'
    }
  ];

  const developmentServices = [
    {
      icon: Code,
      title: 'Web Development Services',
      description: 'Build powerful, scalable web applications with cutting-edge technologies. From responsive websites to complex enterprise solutions.',
      features: ['React & Next.js Development', 'Full-Stack Solutions', 'E-commerce Platforms', 'Custom Web Applications']
    },
    {
      icon: Server,
      title: 'Hosting Services',
      description: 'Reliable, secure, and high-performance hosting solutions. Keep your applications running smoothly with our managed hosting services.',
      features: ['Cloud Hosting Solutions', 'Dedicated Servers', 'SSL Certificates', '24/7 Monitoring & Support']
    },
    {
      icon: Palette,
      title: 'UI/UX Designing',
      description: 'Create stunning, user-friendly interfaces that engage and convert. Our designers craft experiences that users love.',
      features: ['User Interface Design', 'User Experience Research', 'Wireframing & Prototyping', 'Brand Identity Design']
    },
    {
      icon: Megaphone,
      title: 'Digital Marketing Services',
      description: 'Amplify your online presence and reach your target audience effectively with comprehensive digital marketing strategies.',
      features: [
        'Meta Business Suite Management',
        'Google Ads Campaign Management',
        'Content Creation & Strategy',
        'Social Media Marketing',
        'SEO Optimization',
        'Analytics & Reporting'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />

      <div className="py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-20"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
              className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6"
            >
              <span className="text-cyan-500 text-sm font-medium">Our Services</span>
            </motion.div>

            <h1 className="text-4xl md:text-6xl font-semibold tracking-tight mb-6">
              Comprehensive <span className="gradient-text">Solutions</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto leading-relaxed">
              From connecting exceptional talent to building cutting-edge digital solutions,
              OCEANTECH provides end-to-end services to help your business thrive.
            </p>
          </motion.div>

          {/* Recruitment and Staffing Section */}
          {mainServices.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="mb-16"
              >
                <div className={`glass-card rounded-3xl p-8 md:p-12 bg-gradient-to-br ${service.gradient}`}>
                  <div className="flex flex-col md:flex-row gap-8 items-start">
                    <div className="w-16 h-16 rounded-2xl bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-8 h-8 text-cyan-500" />
                    </div>

                    <div className="flex-1">
                      <h2 className="text-3xl md:text-4xl font-semibold mb-4">{service.title}</h2>
                      <p className="text-slate-300 text-lg leading-relaxed mb-6">
                        {service.description}
                      </p>

                      <div className="grid md:grid-cols-2 gap-4">
                        {service.features.map((feature, idx) => (
                          <motion.div
                            key={idx}
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.3 + idx * 0.05 }}
                            className="flex items-center gap-3"
                          >
                            <div className="w-2 h-2 rounded-full bg-cyan-500" />
                            <span className="text-slate-300">{feature}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}

          {/* Development Services Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-5xl font-semibold mb-4">
                Development <span className="gradient-text">Services</span>
              </h2>
              <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                Transform your digital presence with our comprehensive development and marketing solutions
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {developmentServices.map((service, index) => {
                const Icon = service.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -5 }}
                    className="glass-card rounded-2xl p-8 hover:border-cyan-500/30 transition-all duration-300"
                  >
                    <div className="w-14 h-14 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-6">
                      <Icon className="w-7 h-7 text-cyan-500" />
                    </div>

                    <h3 className="text-2xl font-semibold mb-3">{service.title}</h3>
                    <p className="text-slate-400 leading-relaxed mb-6">
                      {service.description}
                    </p>

                    <div className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <Zap className="w-4 h-4 text-cyan-500 mt-1 flex-shrink-0" />
                          <span className="text-slate-300 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-3xl p-12 text-center mt-16 bg-gradient-to-br from-cyan-500/10 to-blue-500/10"
          >
            <Target className="w-12 h-12 text-cyan-500 mx-auto mb-6" />
            <h2 className="text-3xl font-semibold mb-4">Ready to Get Started?</h2>
            <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
              Whether you're looking for top talent or need cutting-edge digital solutions,
              we're here to help your business succeed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact" className="btn-primary px-8 py-3 text-base">
                Contact Us
              </a>
              <a href="/jobs" className="px-8 py-3 rounded-full border border-slate-700 hover:border-cyan-500 transition-colors">
                Browse Jobs
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ServicesPage;
